import { BarChart3, FileText, MapPin, Star, Trash2 } from 'lucide-react';
import React from 'react';
import defaultLogo from '../../assets/images/ci-logo.png';

/**
 * Modern Candidate Card Component
 * Based on conversation-mockup.html design
 *
 * @param {Object} props
 * @param {Object} props.candidate - Candidate data
 * @param {boolean} props.selected - Selection state
 * @param {Function} props.onSelect - Selection callback
 * @param {Function} props.onRemove - Remove callback
 * @param {Function} props.onViewEvaluation - View evaluation callback
 * @param {boolean} props.isEvaluationResult - Is this an evaluation result
 * @param {boolean} props.expanded - CV expansion state
 * @param {Function} props.onToggleExpand - Toggle expansion callback
 * @param {React.Component} props.expandedContent - Expanded CV content
 */
export function CandidateCard({
  candidate,
  selected = false,
  onSelect,
  onRemove,
  onViewEvaluation,
  isEvaluationResult = false,
  expanded = false,
  onToggleExpand,
  expandedContent
}) {
  const getDisplayName = (candidate) => {
    if (candidate.contactName) return candidate.contactName;
    if (candidate.name && candidate.name !== "Unknown") return candidate.name;
    const fileName = candidate.id?.split('/').pop() || '';
    return fileName.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ") || "Unnamed Candidate";
  };

  const getProfileImage = (candidate) => {
    return candidate?.photo || defaultLogo;
  };

  const getMatchBadgeClass = (score) => {
    if (score >= 90) return 'bg-gradient-to-r from-green-500 to-green-600 text-white';
    if (score >= 80) return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
    return 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white';
  };

  const formatScore = (score) => {
    // Handle both decimal (0.95) and percentage (95) formats
    if (score <= 1) {
      return (score * 100).toFixed(0);
    }
    return score.toFixed(0);
  };

  const displayScore = isEvaluationResult ? candidate.score : candidate.combined_score || candidate.score;

  return (
    <div className="candidate-card border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition relative bg-white">
      {/* Selection Checkbox */}
      <div className="absolute top-4 left-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
        />
      </div>

      <div className="flex items-start gap-4 ml-8">
        {/* Avatar */}
        <div className="shrink-0">
          <img
            src={getProfileImage(candidate)}
            alt={getDisplayName(candidate)}
            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-gray-900 text-base">
              {getDisplayName(candidate)}
            </h4>
            <span className={`${getMatchBadgeClass(displayScore)} text-xs px-2 py-1 rounded-full font-medium`}>
              {formatScore(displayScore)}% Match
            </span>
            {/* Contact History Badge */}
            {candidate.contactHistory?.contacted && (
              <span className="px-2 py-1 bg-yellow-200 text-yellow-900 text-xs font-medium rounded-full">
                Previously Contacted
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-3">
            {candidate.title || 'No title available'}
            {candidate.location && ` • ${typeof candidate.location === 'string' ? candidate.location : `${candidate.location.city}, ${candidate.location.country}`}`}
          </p>

          {/* Details Grid - Only for query results */}
          {!isEvaluationResult && candidate.similarity !== undefined && (
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span>Similarity: {formatScore(candidate.similarity)}%</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                <span>Keywords: {candidate.keyword_matches || candidate.keyword_match_count || 0}</span>
              </div>
            </div>
          )}

          {/* Skills */}
          <div className="flex flex-wrap gap-2 mb-3">
            {(candidate.matchingContext?.key_matches || candidate.rating?.key_matches || [])
              .slice(0, 3)
              .map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium"
                >
                  {skill}
                </span>
              ))}
            {(candidate.matchingContext?.key_matches || candidate.rating?.key_matches || []).length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                +{(candidate.matchingContext?.key_matches || candidate.rating?.key_matches || []).length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={() => onToggleExpand && onToggleExpand()}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            <span>{expanded ? 'Hide' : 'View'} CV</span>
          </button>

          {isEvaluationResult && candidate.rating && (
            <button
              onClick={() => onViewEvaluation && onViewEvaluation(candidate)}
              className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              <span>View Evaluation</span>
            </button>
          )}

          <button
            onClick={() => onRemove && onRemove(candidate.id)}
            className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Remove</span>
          </button>
        </div>
      </div>

      {/* Expanded CV Section */}
      {expanded && expandedContent && (
        <div className="mt-4 border-t border-gray-200 pt-4 animate-fade-in">
          {expandedContent}
        </div>
      )}
    </div>
  );
}

export default CandidateCard;

